/**
 * SIMULATED exchange prices and the simulated Betfair reconciliation pair.
 *
 * No exchange price feed is connected to this console. Everything in this file
 * is generated from the fixture's own probabilities so the ladder and the hedge
 * rows are realistic to design against — they are not the market, they are not a
 * Betfair book, and nothing reconciles or settles against them.
 *
 * ── The one rule that makes shipping this defensible ────────────────────────
 * Every generator here is seeded by `marketId` ALONE. Never `world.now`, never a
 * counter, never `Date`. A figure that changed on every 30-second poll would
 * read as a live feed, and "it looks live" is precisely the impression that
 * makes a stubbed exchange book dangerous. These numbers visibly do not move,
 * and the operator can prove that to themselves by watching them.
 *
 * The second half of the safety property is in the types: `RunnerPrices` and
 * `SimulatedExchangeBook` both carry `simulated: true`, so a renderer cannot
 * take this data without the compiler naming what it is.
 */

import { createRng, quantise } from './rng'
import type { BookPosition, RunnerPrices, SimulatedExchangeBook } from './types'
import { round2, tick, tickDown, tickUp } from '@/lib/book'

/** Levels per side. The showcase ladder is 3 back + 3 lay on desktop. */
const DEPTH = 3

/** Half-spread applied either side of fair value before snapping to a tick. */
const HALF_SPREAD = 0.005

/**
 * A 3-deep two-sided ladder per runner, priced off the fixture's true
 * probabilities.
 *
 * `back` comes out outermost-first and `lay` best-first, matching the ordering
 * documented on `RunnerPrices` — the caller renders the array left to right and
 * gets the best two prices adjacent across the spread.
 *
 * Two properties are worth asserting in review, and are pinned in
 * `prices.test.ts`:
 *   1. `bestBack < bestLay` for every runner of every market. A crossed book is
 *      an arbitrage, and an arbitrage on screen is a bug an operator will chase.
 *   2. Sizes are irregular across depths. The recording shows 23,861 / 78,530 /
 *      6,341 — real depth is not monotonic, and a tidy descending ladder would
 *      look synthetic in the one way that matters.
 */
export function simulatedLadder(
  marketId: string,
  selections: string[],
  probs: number[],
): RunnerPrices[] {
  const rng = createRng(`prices:${marketId}`)

  return selections.map((selection, i) => {
    const prob = probs[i]
    // 0.02 floor: a 500/1 outsider still has to price somewhere sane, and a
    // missing probability must not become Infinity.
    const mid = 1 / Math.max(0.02, Number.isFinite(prob) ? (prob as number) : 0.5)

    const bestBack = tick(mid * (1 - HALF_SPREAD))
    // Snapping can collapse the spread on a coarse band (both sides land on 50).
    // Push the lay side out one tick rather than emit a crossed book.
    const snappedLay = tick(mid * (1 + HALF_SPREAD))
    const bestLay = snappedLay > bestBack ? snappedLay : tickUp(bestBack)

    const size = () => round2(quantise(rng.logNormal(40_000, 1.1), 0.01))

    // Draw in render order so the ladder is stable under any future change to
    // how many levels are shown.
    const back = Array.from({ length: DEPTH }, (_, d) => ({
      price: tickDown(bestBack, DEPTH - 1 - d),
      size: size(),
    }))
    const lay = Array.from({ length: DEPTH }, (_, d) => ({
      price: tickUp(bestLay, d),
      size: size(),
    }))

    return { selection, back, lay, simulated: true }
  })
}

/**
 * The simulated "Betfair book" pair — what the exchange says the hedge is, and
 * what the platform's own record says it is.
 *
 * Shape reproduces the recording: one runner green, the others red, similar
 * magnitude, and a sub-0.1% gap between the two rows. A non-zero difference is
 * what a reconciliation screen exists to surface, so the pair must not agree
 * exactly or the row would teach the wrong thing.
 *
 * `bestBackFor` comes from `simulatedLadder`, which is why the hedge inherits
 * the same stability: same market id, same figures, forever.
 */
export function simulatedExchangeBook(
  marketId: string,
  positions: BookPosition[],
  bestBackFor: (selection: string) => number | null,
): SimulatedExchangeBook {
  if (positions.length === 0) return { simulated: true, exchange: [], local: [] }

  const rng = createRng(`hedge:${marketId}`)

  let worstIndex = 0
  for (let i = 1; i < positions.length; i++) {
    if (positions[i]!.net < positions[worstIndex]!.net) worstIndex = i
  }
  const worst = positions[worstIndex]!
  const price = bestBackFor(worst.name)

  // Draw the fraction unconditionally so an unusable price cannot shift the
  // stream and change the noise on a different market.
  const fraction = rng.float(0.03, 0.09)
  const usable = price !== null && Number.isFinite(price) && price > 1.01
  const stake = usable ? round2(Math.abs(Math.min(0, worst.net)) * fraction) : 0

  const exchange: BookPosition[] = positions.map((p, i) => ({
    name: p.name,
    net: !usable ? 0 : i === worstIndex ? round2(stake * ((price as number) - 1)) : round2(-stake),
  }))

  const local: BookPosition[] = exchange.map((p) => ({
    name: p.name,
    net: round2(p.net * (1 + rng.float(-0.004, 0.004))),
  }))

  return { simulated: true, exchange, local }
}
